import type { StudyThreadSelectDto } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import { useGetMeiaThreadSwimlanes, useGetMeiaThreadTaskReadiness } from '@queries/meia.query';
import { getStudyThreadKillChainLabel } from '@utils/meia-enum.util';
import type { FC, ReactNode } from 'react';
import { getMeiaThreadReadinessStatus, getMeiaThreadReadinessTooltip } from './meia-readiness.util';
import { MeiaTaskReadinessStatusIcon } from './meia-task-readiness-status-icon.component';
import { getStudyThreadRoleBadgeVariant, getStudyThreadRoleLabel } from './meia-thread-role.util';

/** Shared tile title for the thread picker, used by both the P2 manager and the P3/P5 selector. */
export const MeiaThreadPickerTileTitle: FC = () => (
  <span className="inline-flex items-center gap-1.5">
    Mission Threads
    <TooltipComponent side="top">Select a Mission Thread to operate on its context.</TooltipComponent>
  </span>
);

type MeiaThreadPickerCardShellProps = {
  /** Optional hover-revealed control (e.g. an edit IconButton); rendered top-right. */
  editButton?: ReactNode;
  isSelected: boolean;
  /** Small gray line under the role badge (e.g. `F2T2EA · 3 tasks`). */
  metaLine: string;
  onSelect: () => void;
  role: StudyThreadSelectDto['role'];
  /** Status visual rendered at the top-left (readiness icon on P2/P3/P5, verdict score on P4). */
  statusIndicator: ReactNode;
  title: string;
};

/**
 * Presentational Mission Thread card shell: status indicator + name, the role badge
 * directly under the title, and a small meta line. Shared by the live picker card
 * (P2/P3/P5) and the P4 Scoring thread picker so the card visuals cannot drift.
 */
export const MeiaThreadPickerCardShell: FC<MeiaThreadPickerCardShellProps> = (props) => {
  const { editButton, isSelected, metaLine, onSelect, role, statusIndicator, title } = props;

  return (
    <div
      className={cn(
        'group relative flex min-w-[200px] max-w-[280px] flex-col rounded-md transition-all duration-200',
        isSelected
          ? 'bg-primary/15 text-primary ring-2 ring-inset ring-primary/80'
          : 'bg-gray-100 text-gray-700 hover:ring-2 hover:ring-primary/80',
      )}
    >
      <button
        className="flex cursor-pointer flex-col items-start gap-1.5 p-3 text-left"
        onClick={onSelect}
        type="button"
      >
        <div className="flex min-w-0 items-start gap-2">
          {statusIndicator}
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className={cn('line-clamp-2 text-sm font-semibold', editButton && 'pr-6')}>{title}</span>
            <Badge
              className="w-fit"
              size="xs"
              variant={getStudyThreadRoleBadgeVariant(role)}
            >
              {getStudyThreadRoleLabel(role)}
            </Badge>
            <span className="text-[11px] text-gray-500">{metaLine}</span>
          </div>
        </div>
      </button>
      {editButton && (
        <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">{editButton}</div>
      )}
    </div>
  );
};

type MeiaThreadPickerCardProps = {
  /** Optional hover-revealed control (e.g. an edit IconButton); rendered top-right. */
  editButton?: ReactNode;
  isSelected: boolean;
  onSelect: () => void;
  studyId: string;
  thread: StudyThreadSelectDto;
};

/**
 * Canonical selectable Mission Thread card: readiness status icon + name, the
 * role badge directly under the title, and the kill-chain pattern rendered as
 * plain text alongside the task count. Shared so the P2 Thread manager and the
 * P3/P5 thread selector stay visually identical.
 */
export const MeiaThreadPickerCard: FC<MeiaThreadPickerCardProps> = (props) => {
  const { editButton, isSelected, onSelect, studyId, thread } = props;
  const { data: swimlanes = [] } = useGetMeiaThreadSwimlanes({ studyId, threadId: thread.id });
  const { data: taskReadiness = [] } = useGetMeiaThreadTaskReadiness({ studyId, threadId: thread.id });
  const status = getMeiaThreadReadinessStatus({ swimlanes, taskReadiness });
  const statusTooltip = getMeiaThreadReadinessTooltip(status);
  const taskCount = taskReadiness.length;

  return (
    <MeiaThreadPickerCardShell
      editButton={editButton}
      isSelected={isSelected}
      metaLine={`${getStudyThreadKillChainLabel(thread.killChainCategory)} · ${taskCount} ${
        taskCount === 1 ? 'task' : 'tasks'
      }`}
      onSelect={onSelect}
      role={thread.role}
      statusIndicator={
        <MeiaTaskReadinessStatusIcon
          className="mt-px"
          status={status}
          tooltip={statusTooltip}
        />
      }
      title={thread.name}
    />
  );
};
